import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import expandIcon from '../../../../../src/images/expandIcon.svg';

export const enum ContentType {
  VIMEO,
  DATA,
}

interface VisualisationBlockProps {
  url?: string;
  className?: string;
  contentType: ContentType;
  dataImageSrc?: any;
}

const VisualisationBlock: React.FC<VisualisationBlockProps> = ({
  className,
  contentType,
  dataImageSrc,
  url,
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(
          `Error attempting to enable full-screen mode: ${err.message}`
        );
      });
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
    }
    setIsFullScreen(!isFullScreen);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(document.fullscreenElement !== null);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleKeydown = event => {
      if (event.key === 'Escape' && isFullScreen) {
        toggleFullScreen();
      }
    };

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isFullScreen]);

  return (
    <div className={className}>
      <div className="wrapper">
        <div className={`video-player ${isFullScreen ? 'full-screen' : ''}`}>
          <div
            className={`video-container ${isFullScreen ? 'full-screen' : ''}`}
          >
            {contentType == ContentType.VIMEO && (
              <iframe
                src={url}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen={true}
                title="S01-English"
              ></iframe>
            )}
            {contentType == ContentType.DATA && dataImageSrc && (
              <div className="data-image">
                <img src={dataImageSrc}></img>
              </div>
            )}
          </div>
          <div
            className={`fullscreen-icon ${isFullScreen ? 'full-screen' : ''}`}
            onClick={toggleFullScreen}
          >
            <img src={expandIcon} className="expand-icon"></img>
          </div>
        </div>
        <script src="https://player.vimeo.com/api/player.js"></script>
      </div>
    </div>
  );
};

const StyledVisualisationBlock = styled(VisualisationBlock)`
  .wrapper {
    display: flex;
    align-items: flex-end;
  }

  .expand-icon {
    width: 70%;
  }

  .video-player {
    background-color: ${vars.greyHavens};
    border-radius: 8px;
    position: relative;
    width: 100%;
    background-color: white;
    overflow: hidden;
    
  }

  .video-container {
    position: relative;
    padding-bottom: 73.5%; /* close to 4:3 aspect ratio, depending on the content */
    height: 0;
  }

  .video-container.full-screen {
    height: 100vh;
    width: 100vw;
    padding-bottom: 0;
    overflow-y: hidden; 
  }

  iframe {
    border: none;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    position: absolute;
  }

  .video-player.full-screen {
    border-radius: 0px;
    background-color: #000000;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 500;
    overflow-y: hidden; 
  }
  
  .video-player.full-screen iframe {
    width: 100%
    height: 100%
    object-fit: contain;
    overflow-y: hidden; 
  }
  
  .data-image {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow-x: auto;
    white-space: nowrap;
    display: flex;
    min-width: 28rem;

    // Firefox
    scrollbar-width: thick;
    scrollbar-color: darkgray white;
  }

  // Chrome
  .data-image::-webkit-scrollbar {
    width: 100px;
    height: 10px;
  }

  .data-image::-webkit-scrollbar-thumb {
    background-color: darkgray;
    border-radius: 5px;
  }
  
  .data-image img {
    max-height: 100%;
  }
  
  .fullscreen-icon {
    position: absolute;
    bottom: 12px;
    right: 10px;
    width: 40px;
    height: 40px;
    background-color: white;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    color: #ffffff;
    font-size: 20px;
    margin-left: -50px;
    z-index: 501; // TODO: change to lower or relative number
  }
  .fullscreen-icon.full-screen {
    position: absolute;
    width: 60px;
    height: 60px;
  }
`;

export default StyledVisualisationBlock;
